import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowUpCircle,
  ChevronDown,
  ChevronUp,
  History,
  Package,
  Pencil,
  Plus,
  Settings,
  Trash2,
  X,
} from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { EmptyState } from "@/components/EmptyState";
import { FormField, MoneyField, NumberField } from "@/components/FormField";
import { SuppliesWizard } from "@/components/SuppliesWizard";
import {
  generateId,
  useApp,
  type RestockEvent,
  type ShippingSupply,
  type SuppliesTrackingMode,
  type SupplyConsumption,
} from "@/context/AppContext";
import { ManagerOnly } from "@/components/ManagerOnly";

const MODE_LABEL: Record<SuppliesTrackingMode, string> = {
  off: "No tracking",
  "cost-only": "Cost per use",
  full: "Full history",
};

// NOTE (TNK-48): move this to a Settings "low-stock threshold" preference
// (appPreferences slice). See docs/settings-audit.md.
const LOW_STOCK_THRESHOLD = 5;

export const Route = createFileRoute("/tools/supplies")({
  component: () => (
    <ManagerOnly>
      <SuppliesPage />
    </ManagerOnly>
  ),
});

function SuppliesPage() {
  const {
    shippingSupplies,
    addShippingSupply,
    updateShippingSupply,
    deleteShippingSupply,
    restockSupply,
    appPreferences,
  } = useApp();

  // Resolve tracking mode for this store. Legacy stores that pre-date the
  // wizard have no `supplies` prefs but may already have enabled supplies +
  // restock history — treat them as `full` + initialized so they skip the
  // CTA. Fresh stores fall through to !initialized and see the wizard.
  const hasLegacyEnabled = shippingSupplies.some((s) => s.enabled);
  const initialized = appPreferences.supplies?.initialized ?? hasLegacyEnabled;
  const trackingMode: SuppliesTrackingMode =
    appPreferences.supplies?.trackingMode ??
    (hasLegacyEnabled ? "full" : "off");

  const [wizardOpen, setWizardOpen] = useState(false);
  // First-time visit on a fresh store auto-opens the wizard; once dismissed
  // it stays closed until the user clicks Set up / Re-initialize.
  useEffect(() => {
    if (!initialized && !wizardOpen) setWizardOpen(true);
    // We deliberately only react to `initialized` flips, not wizardOpen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized]);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [quantity, setQuantity] = useState("0");
  const [unitsPerUseInput, setUnitsPerUseInput] = useState("1");
  const [consumptionInput, setConsumptionInput] =
    useState<SupplyConsumption>("per-listing");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [restockMode, setRestockMode] = useState(false);
  const [restockBatches, setRestockBatches] = useState<
    Record<string, { totalCost: string; units: string; supplier: string }>
  >({});
  const [restockSaving, setRestockSaving] = useState(false);
  const [restockError, setRestockError] = useState<string | null>(null);
  const [historyExpanded, setHistoryExpanded] = useState<Set<string>>(
    new Set(),
  );

  const setRestockBatch = (
    id: string,
    patch: Partial<{ totalCost: string; units: string; supplier: string }>,
  ) => {
    setRestockBatches((prev) => {
      const current = prev[id] ?? { totalCost: "", units: "", supplier: "" };
      return { ...prev, [id]: { ...current, ...patch } };
    });
  };

  const toggleHistory = (id: string) => {
    setHistoryExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const restockTotals = useMemo(() => {
    let units = 0;
    let cost = 0;
    for (const batch of Object.values(restockBatches)) {
      const u = parseInt(batch.units, 10);
      const c = parseFloat(batch.totalCost);
      if (!isNaN(u) && u > 0 && !isNaN(c) && c >= 0) {
        units += u;
        cost += c;
      }
    }
    return { units, cost };
  }, [restockBatches]);

  const openRestock = () => {
    setRestockBatches({});
    setRestockError(null);
    setRestockMode(true);
    setShowForm(false);
    setEditingId(null);
    setConfirmDeleteId(null);
  };

  const closeRestock = () => {
    setRestockMode(false);
    setRestockBatches({});
    setRestockError(null);
  };

  // Weighted-average math now runs server-side (TNK-100). Each filled batch
  // becomes one POST /shipping-supplies/:id/restock call; the response
  // overwrites the local supply with the freshly blended unitCost + full
  // history. We await all calls before closing so the UI reflects the new
  // state in one paint.
  const applyRestock = async () => {
    if (restockSaving) return;
    const valid: Array<{
      supplyId: string;
      addedQty: number;
      totalCost: number;
      supplier?: string;
    }> = [];
    for (const supply of shippingSupplies) {
      const batch = restockBatches[supply.id];
      if (!batch) continue;
      const addedUnits = parseInt(batch.units, 10);
      const totalCost = parseFloat(batch.totalCost);
      if (isNaN(addedUnits) || addedUnits <= 0) continue;
      if (isNaN(totalCost) || totalCost < 0) continue;
      const supplier = batch.supplier.trim();
      valid.push({
        supplyId: supply.id,
        addedQty: addedUnits,
        totalCost,
        supplier: supplier || undefined,
      });
    }
    if (valid.length === 0) {
      closeRestock();
      return;
    }
    setRestockSaving(true);
    setRestockError(null);
    try {
      await Promise.all(
        valid.map(({ supplyId, addedQty, totalCost, supplier }) =>
          restockSupply(supplyId, { addedQty, totalCost, supplier }),
        ),
      );
      closeRestock();
    } catch (e) {
      setRestockError(
        e instanceof Error ? e.message : "Could not save restock — try again.",
      );
    } finally {
      setRestockSaving(false);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setName("");
    setUnitCost("");
    setQuantity("0");
    setUnitsPerUseInput("1");
    setConsumptionInput("per-listing");
    setErrors({});
    setShowForm(true);
    setRestockMode(false);
  };

  const openEdit = (supply: ShippingSupply) => {
    setEditingId(supply.id);
    setName(supply.name);
    setUnitCost(supply.unitCost.toFixed(2));
    setQuantity(String(supply.quantity ?? 0));
    setUnitsPerUseInput(String(supply.unitsPerUse ?? 1));
    setConsumptionInput(supply.consumption ?? "per-listing");
    setErrors({});
    setShowForm(true);
    setRestockMode(false);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleSave = () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = "Name is required";
    // Cost is only present when tracking it (cost-only / full).
    const cost = trackingMode === "off" ? 0 : parseFloat(unitCost);
    if (trackingMode !== "off") {
      if (!unitCost.trim() || isNaN(cost) || cost < 0) {
        errs.unitCost = "Enter a valid cost (>= 0)";
      }
    }
    // Quantity only matters when tracking full history.
    const qty = trackingMode === "full" ? parseInt(quantity, 10) : 0;
    if (trackingMode === "full") {
      if (quantity.trim() === "" || isNaN(qty) || qty < 0) {
        errs.quantity = "Enter a valid quantity (>= 0)";
      }
    }
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    const resolvedUnitsPerUse = Math.max(
      1,
      parseInt(unitsPerUseInput, 10) || 1,
    );
    if (editingId) {
      const existing = shippingSupplies.find((s) => s.id === editingId);
      if (existing) {
        updateShippingSupply({
          ...existing,
          name: name.trim(),
          unitCost: trackingMode === "off" ? existing.unitCost : cost,
          quantity: trackingMode === "full" ? qty : existing.quantity,
          unitsPerUse:
            trackingMode === "full"
              ? resolvedUnitsPerUse
              : existing.unitsPerUse,
          consumption:
            trackingMode === "full" ? consumptionInput : existing.consumption,
        });
      }
    } else {
      addShippingSupply({
        id: generateId("SUP"),
        name: name.trim(),
        unitCost: cost,
        quantity: qty,
        enabled: true,
        unitsPerUse: trackingMode === "full" ? resolvedUnitsPerUse : 1,
        consumption: trackingMode === "full" ? consumptionInput : "per-listing",
        createdAt: new Date().toISOString(),
      });
    }
    closeForm();
  };

  const handleDelete = (id: string) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      return;
    }
    deleteShippingSupply(id);
    setConfirmDeleteId(null);
  };

  // The page lists only enabled supplies (everything else lives on the catalog
  // for the wizard to expose). Add/Edit/Delete in this page still operate on
  // the underlying shippingSupplies array so the wizard can also see them.
  const visibleSupplies = useMemo(
    () => shippingSupplies.filter((s) => s.enabled),
    [shippingSupplies],
  );

  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/tools" }} label="Tools" />
        {!showForm && !restockMode && initialized && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setWizardOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-semibold text-foreground hover:bg-muted"
              title="Re-run the supplies init wizard"
            >
              <Settings className="h-3.5 w-3.5" />
              Re-initialize
            </button>
            {trackingMode === "full" && visibleSupplies.length > 0 && (
              <button
                type="button"
                onClick={openRestock}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-semibold text-foreground hover:bg-muted"
                title="Add stock to multiple supplies at once"
              >
                <ArrowUpCircle className="h-3.5 w-3.5" />
                Restock
              </button>
            )}
            <button
              type="button"
              onClick={openAdd}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              <Plus className="h-4 w-4" />
              Add Supply
            </button>
          </div>
        )}
      </div>

      <div className="border-b border-border pb-3">
        <h1 className="text-2xl font-bold text-foreground">
          Packaging Supplies
        </h1>
        <p className="text-sm text-muted-foreground">
          Track sleeves, toploaders, mailers, and other consumables. Select them
          when going live to add per-listing costs automatically.
        </p>
        {initialized && (
          <p className="mt-1 text-[11px] uppercase tracking-wider text-muted-foreground">
            Tracking mode: {MODE_LABEL[trackingMode]}
          </p>
        )}
      </div>

      <div
        className="flex-1 space-y-2 overflow-y-auto py-4 pr-1"
        style={{ scrollbarGutter: "stable" }}
      >
        {restockMode && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-2">
            <div className="flex items-center gap-2">
              <ArrowUpCircle className="h-4 w-4 text-primary" />
              <div className="text-sm font-semibold text-foreground">
                Restock mode
              </div>
              <div className="text-xs text-muted-foreground">
                Enter total cost + units per supply
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={closeRestock}
                className="inline-flex items-center gap-1 rounded-md bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground hover:bg-muted/70"
              >
                <X className="h-3 w-3" />
                Cancel
              </button>
              <button
                type="button"
                onClick={applyRestock}
                disabled={restockTotals.units === 0 || restockSaving}
                className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-40"
              >
                {restockSaving ? (
                  <>Saving…</>
                ) : (
                  <>
                    Apply (+{restockTotals.units}{" "}
                    {restockTotals.units === 1 ? "unit" : "units"} · $
                    {restockTotals.cost.toFixed(2)})
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {restockMode && restockError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {restockError}
          </div>
        )}

        {showForm && (
          <div className="space-y-3 rounded-md border border-border bg-card p-4">
            <div className="text-sm font-semibold text-foreground">
              {editingId ? "Edit Supply" : "Add Supply"}
            </div>

            <FormField
              label="Name"
              value={name}
              onChangeText={(v) => {
                setName(v);
                setErrors((e) => ({ ...e, name: "" }));
              }}
              placeholder="e.g. Bubble Mailer, Top Loader, Penny Sleeve"
              error={errors.name}
            />
            {trackingMode !== "off" && (
              <MoneyField
                label={
                  trackingMode === "cost-only" ? "Cost per use" : "Unit Cost"
                }
                value={unitCost}
                onChangeText={(v) => {
                  setUnitCost(v);
                  setErrors((e) => ({ ...e, unitCost: "" }));
                }}
                error={errors.unitCost}
              />
            )}
            {trackingMode === "full" && (
              <NumberField
                label="Quantity in Stock"
                value={quantity}
                onChangeText={(v) => {
                  setQuantity(v);
                  setErrors((e) => ({ ...e, quantity: "" }));
                }}
                placeholder="0"
                error={errors.quantity}
              />
            )}
            {trackingMode === "full" && (
              <div className="mb-4 flex flex-col gap-1.5">
                <label className="text-xs font-medium tracking-wide text-muted-foreground">
                  Units consumed
                </label>
                <div className="flex items-stretch gap-1.5">
                  <div className="flex items-center rounded-md border border-border bg-card px-3 focus-within:border-primary">
                    <input
                      value={unitsPerUseInput}
                      onChange={(e) =>
                        setUnitsPerUseInput(e.target.value.replace(/\D/g, ""))
                      }
                      inputMode="numeric"
                      placeholder="1"
                      className="min-h-[44px] w-16 bg-transparent py-3 text-center text-[15px] text-foreground placeholder:text-muted-foreground focus:outline-none"
                    />
                  </div>
                  <select
                    value={consumptionInput}
                    onChange={(e) =>
                      setConsumptionInput(e.target.value as SupplyConsumption)
                    }
                    className="min-h-[44px] flex-1 rounded-md border border-border bg-card px-3 text-[15px] text-foreground focus:border-primary focus:outline-none"
                  >
                    <option
                      value="per-listing"
                      className="bg-card text-foreground"
                    >
                      per listing (typical for mailers)
                    </option>
                    <option
                      value="per-card"
                      className="bg-card text-foreground"
                    >
                      per card (e.g. penny sleeves)
                    </option>
                  </select>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={closeForm}
                className="flex-1 rounded-md bg-muted px-3 py-2 text-sm font-semibold text-muted-foreground hover:bg-muted/70"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="flex-[2] rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
              >
                {editingId ? "Save Changes" : "Add Supply"}
              </button>
            </div>
          </div>
        )}

        {!initialized && !showForm && (
          <EmptyState
            icon={<Package className="h-8 w-8" />}
            title="Set up your packing supplies"
            description="Pick which items you carry and how detailed you want bookkeeping to be."
            actionLabel="Start setup"
            onAction={() => setWizardOpen(true)}
          />
        )}

        {initialized && visibleSupplies.length === 0 && !showForm && (
          <EmptyState
            icon={<Package className="h-8 w-8" />}
            title="No enabled supplies"
            description="Run Re-initialize to pick which supplies you carry, or add a custom supply."
            actionLabel="Add Supply"
            onAction={openAdd}
          />
        )}

        {visibleSupplies.map((supply) => {
          const isConfirming = confirmDeleteId === supply.id;
          const qty = supply.quantity ?? 0;
          const totalVal = qty * supply.unitCost;
          const isLowStock = qty < LOW_STOCK_THRESHOLD;
          const batch = restockBatches[supply.id] ?? {
            totalCost: "",
            units: "",
            supplier: "",
          };
          const batchUnits = parseInt(batch.units, 10);
          const batchCost = parseFloat(batch.totalCost);
          const batchValid =
            !isNaN(batchUnits) &&
            batchUnits > 0 &&
            !isNaN(batchCost) &&
            batchCost >= 0;
          const batchPerUnit = batchValid ? batchCost / batchUnits : null;
          const projectedQty = batchValid ? qty + batchUnits : qty;

          if (restockMode) {
            return (
              <div
                key={supply.id}
                className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card px-3 py-2"
              >
                <div className="min-w-0 flex-[2_1_180px]">
                  <div className="truncate text-sm font-semibold text-foreground">
                    {supply.name}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {qty} in stock · ${supply.unitCost.toFixed(2)}/u
                  </div>
                </div>
                <div className="flex items-center gap-1 rounded-md bg-muted px-2">
                  <span className="text-[11px] text-muted-foreground">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={batch.totalCost}
                    onChange={(e) =>
                      setRestockBatch(supply.id, {
                        totalCost: e.target.value
                          .replace(/[^0-9.]/g, "")
                          .replace(/(\..*)\./g, "$1"),
                      })
                    }
                    placeholder="Total"
                    className="w-20 bg-transparent py-1.5 text-center text-sm text-foreground outline-none placeholder:text-muted-foreground"
                  />
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={batch.units}
                  onChange={(e) =>
                    setRestockBatch(supply.id, {
                      units: e.target.value.replace(/\D/g, ""),
                    })
                  }
                  placeholder="Units"
                  className="w-16 rounded-md bg-muted px-2 py-1.5 text-center text-sm text-foreground outline-none placeholder:text-muted-foreground"
                />
                <input
                  type="text"
                  value={batch.supplier}
                  onChange={(e) =>
                    setRestockBatch(supply.id, { supplier: e.target.value })
                  }
                  placeholder="Supplier (optional)"
                  className="w-32 rounded-md bg-muted px-2 py-1.5 text-sm text-foreground outline-none placeholder:text-muted-foreground"
                />
                {batchValid && batchPerUnit != null ? (
                  <div className="ml-auto text-right text-[11px] text-muted-foreground">
                    ${batchPerUnit.toFixed(2)}/u · → {projectedQty} in stock
                  </div>
                ) : (
                  <div className="ml-auto text-right text-[11px] text-muted-foreground/60">
                    enter cost + units
                  </div>
                )}
              </div>
            );
          }

          // Only warn about stock once the supply has actually had stock at
          // some point — a brand-new enabled supply that's never been
          // restocked isn't "out of stock", it just hasn't been used yet.
          const hasBeenStocked =
            (supply.quantity ?? 0) > 0 ||
            (supply.restockHistory?.length ?? 0) > 0;
          const showLowStockBadge =
            trackingMode === "full" && isLowStock && hasBeenStocked;
          return (
            <div
              key={supply.id}
              className={`rounded-md border p-2.5 ${
                isConfirming
                  ? "border-destructive bg-card"
                  : showLowStockBadge
                    ? "border-amber-500/40 bg-amber-500/5"
                    : "border-border bg-card"
              }`}
            >
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <div className="text-sm font-semibold text-foreground">
                      {supply.name}
                    </div>
                    {trackingMode === "cost-only" && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                        · ${supply.unitCost.toFixed(2)} per use
                        <span
                          aria-label="Stock not tracked in cost-only mode"
                          title="Stock not tracked — treated as unlimited in cost-only mode"
                          className="rounded bg-muted px-1 text-[10px] text-muted-foreground"
                        >
                          ∞
                        </span>
                      </span>
                    )}
                    {showLowStockBadge && (
                      <span className="inline-flex items-center gap-1 rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-600">
                        <AlertTriangle className="h-2.5 w-2.5" />
                        {qty === 0 ? "Out of stock" : "Low stock"}
                      </span>
                    )}
                  </div>
                </div>
                {!isConfirming && (
                  <div className="flex shrink-0 items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => openEdit(supply)}
                      className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(supply.id)}
                      className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>

              {trackingMode === "full" && (
                <div className="mt-1.5 flex items-stretch divide-x divide-border">
                  <RowStat
                    label="per use"
                    value={`$${(supply.unitCost * (supply.unitsPerUse ?? 1)).toFixed(2)}`}
                  />
                  <RowStat
                    label="in stock"
                    value={String(qty)}
                    emphasis={isLowStock ? "warn" : undefined}
                  />
                  <RowStat
                    label="total value"
                    value={`$${totalVal.toFixed(2)}`}
                  />
                </div>
              )}

              {trackingMode === "full" &&
                (supply.restockHistory?.length ?? 0) > 0 && (
                  <SupplyHistory
                    events={supply.restockHistory!}
                    expanded={historyExpanded.has(supply.id)}
                    onToggle={() => toggleHistory(supply.id)}
                  />
                )}

              {isConfirming && (
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(null)}
                    className="rounded-md bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground hover:bg-muted/70"
                  >
                    Keep
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(supply.id)}
                    className="rounded-md bg-destructive px-2.5 py-1 text-xs font-semibold text-white hover:opacity-90"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {wizardOpen && (
        <SuppliesWizard
          initialMode={trackingMode}
          onClose={() => setWizardOpen(false)}
        />
      )}
    </section>
  );
}

function RowStat({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: "warn";
}) {
  return (
    <div className="flex flex-1 flex-col items-center px-2">
      <div
        className={`text-xs font-semibold ${
          emphasis === "warn" ? "text-amber-600" : "text-foreground"
        }`}
      >
        {value}
      </div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

function SupplyHistory({
  events,
  expanded,
  onToggle,
}: {
  events: RestockEvent[];
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="mt-2 border-t border-border pt-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground"
      >
        <History className="h-3 w-3" />
        Restock history ({events.length})
        {expanded ? (
          <ChevronUp className="h-3 w-3" />
        ) : (
          <ChevronDown className="h-3 w-3" />
        )}
      </button>
      {expanded && (
        <ul className="mt-1.5 space-y-1">
          {events.map((ev) => {
            const date = new Date(ev.recordedAt).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
            });
            return (
              <li
                key={ev.id}
                className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-muted-foreground"
              >
                <span className="font-medium text-foreground">
                  +{ev.addedQty}
                </span>
                <span>
                  ${ev.totalCost.toFixed(2)} · ${ev.perUnitCost.toFixed(2)}/u
                </span>
                {ev.supplier && (
                  <span className="text-foreground">· {ev.supplier}</span>
                )}
                <span className="ml-auto">{date}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
